/**
 * OpenAI Helper - GPT-4 Integration via Firebase Functions (secure backend proxy)
 *
 * OpenAI calls are routed through the `openaiChat` Firebase Callable Function.
 * The API key lives in Firebase Secrets Manager and is NEVER exposed to the browser.
 *
 * OpenAI Age Policy: 13+ with parental consent
 * Compliance: We restrict Burt to parents (18+) by default
 */

import { httpsCallable } from "firebase/functions";
import { functionsInstance } from "../firebase";

/**
 * Send a message to OpenAI GPT-4 via the secure backend function.
 * @param {string} userMessage - The user's message
 * @param {string} context - Additional context (family data, etc.)
 * @param {string} assistant - Which assistant ('burt' or 'earn-e')
 * @returns {Promise<string>} AI response
 */
export async function sendToOpenAI(userMessage, context = '', assistant = 'burt') {
  try {
    const openaiChat = httpsCallable(functionsInstance, 'openaiChat');
    const result = await openaiChat({ message: userMessage, context, assistant });
    return result.data.reply;
  } catch (error) {
    console.error('Error calling openaiChat function:', error);
    throw error;
  }
}

/**
 * Math and conversion helper using GPT-4
 * Handles measurement conversions, calculations, etc.
 */
export async function getMathConversion(question) {
  try {
    const response = await sendToOpenAI(
      question,
      'The user is asking a math or measurement conversion question. Provide a clear, concise answer with the calculation shown.',
      'burt'
    );
    return response;
  } catch (error) {
    console.error('Math conversion error:', error);
    throw error;
  }
}

/**
 * Recipe generation with OpenAI
 * Returns structured recipe data
 */
export async function generateRecipe(recipeName, familySize = 4) {
  try {
    const prompt = `Create a detailed, family-friendly recipe for "${recipeName}" for ${familySize} people.

Respond EXACTLY in this plain-text format with no markdown, no bold, no headers with # symbols:

[Full recipe name here]

Description: [One sentence describing the dish]
Prep Time: [prep time]
Cook Time: [cook time]
Servings: ${familySize}
Difficulty: [Easy/Medium/Hard]

INGREDIENTS:
- [amount] [unit] [ingredient name]
- [amount] [unit] [ingredient name]

INSTRUCTIONS:
1. [Detailed step]
2. [Detailed step]

TIPS:
- [Practical cooking tip]
- [Practical cooking tip]

Use detailed, clear instructions with specific temperatures and times. Include at least 2 useful tips.`;

    const response = await sendToOpenAI(prompt, '', 'burt');
    return response;
  } catch (error) {
    console.error('Recipe generation error:', error);
    throw error;
  }
}

/**
 * Smart shopping suggestions
 * Analyzes shopping list and provides optimization tips
 */
export async function getShoppingSuggestions(shoppingList) {
  try {
    const items = shoppingList.map(item => item.name || item.item).join(', ');

    const prompt = `I have these items on my shopping list: ${items}

Please provide:
1. Budget-saving tips for these specific items
2. Items I might have forgotten that complement these
3. Best stores or categories to find deals

Keep it brief and practical!`;

    const response = await sendToOpenAI(prompt, '', 'burt');
    return response;
  } catch (error) {
    console.error('Shopping suggestions error:', error);
    throw error;
  }
}

/**
 * Chore suggestions based on family context
 */
export async function getChoresuggestions(familyMembers, existingTasks) {
  try {
    const context = `Family has ${familyMembers.length} members. Existing tasks: ${existingTasks.map(t => t.title).join(', ')}`;

    const prompt = `Based on this family, suggest 5 age-appropriate chores they might be missing. Keep it brief - just list the chore names with a 1-sentence description each.`;

    const response = await sendToOpenAI(prompt, context, 'burt');
    return response;
  } catch (error) {
    console.error('Chore suggestions error:', error);
    throw error;
  }
}

const openaiHelper = {
  sendToOpenAI,
  getMathConversion,
  generateRecipe,
  getShoppingSuggestions,
  getChoreSuggestions: getChoresuggestions
};

export default openaiHelper;
